/**
 * FramedBox.jsx
 *
 * Renders a styled container with a fieldset and legend label.
 * Useful for grouping form elements with a visible frame and a label.
 * Supports style overrides for root, label, and content slots via slotProps.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";

const defaultStyles = {
  root: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    position: 'relative',
    p: 1.5,
    pt: 0,
    width: "100%",
  },
  label: {
    fontSize: 9,
    ml: "-4px",
    padding: "0 15px 5px 5px",
  },
  content: {},
};

/**
 * FramedBox component
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children - Content to render inside the box.
 * @param {string} props.label - Text to display as the legend label.
 * @param {object} [props.slotProps] - Style overrides for internal components.
 * @param {object} [props.slotProps.root] - Styles applied to the root FormControl.
 * @param {object} [props.slotProps.label] - Styles applied to the FormLabel.
 * @param {object} [props.slotProps.content] - Styles applied to the content Box.
 */
const FramedBox = ({
  children,
  label,
  slotProps = {},
}) => {
  const {
    root: rootSlot = {},
    label: labelSlot = {},
    content: contentSlot = {},
  } = slotProps;

  return (
    <FormControl
      component="fieldset"
      variant="outlined"
      sx={{ ...defaultStyles.root, ...rootSlot }}
    >
      <FormLabel
        component="legend"
        sx={{ ...defaultStyles.label, ...labelSlot }}
      >
        {label}
      </FormLabel>
      <Box sx={{ ...defaultStyles.content, ...contentSlot }}>
        {children}
      </Box>
    </FormControl>
  );
};

FramedBox.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  slotProps: PropTypes.shape({
    root: PropTypes.object,
    label: PropTypes.object,
    content: PropTypes.object,
  }),
};

FramedBox.displayName = "FramedBox";

export default FramedBox;
